import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { typeMeta, fmtDate } from './opsTypes';
import {
  Activity, CalendarClock, MessageSquareWarning, AlertTriangle, Flag,
  Wallet, UserX, Clock, FileWarning, CheckCircle2, ExternalLink, Receipt, ClipboardList,
} from 'lucide-react';

const fmtMoney = (v) => `$${(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// A single unified delivery row with a type badge
export const DeliveryRow = ({ e }) => {
  const navigate = useNavigate();
  const m = typeMeta(e.type);
  return (
    <button
      onClick={() => navigate(e.url)}
      className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-border hover:bg-muted/50 transition-colors text-left group"
      data-testid={`ops-delivery-row-${e.type}-${e.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: `${m.color}1A`, color: m.color }}>
          {m.label}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{e.title}</p>
          <p className="text-xs text-muted-foreground truncate">
            {e.partner_name}
            {e.trainers?.length ? ` · ${e.trainers.join(', ')}` : ''}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {e.platform && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 whitespace-nowrap" data-testid="ops-platform-badge">{e.platform}</span>}
        <span className="text-xs text-muted-foreground">{fmtDate(e.start)}</span>
        <ExternalLink className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </button>
  );
};

const SectionCard = ({ title, icon: Icon, iconColor, count, items, emptyText, testId, renderRow }) => (
  <Card data-testid={testId} className="flex flex-col">
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        {title}
        <Badge variant="outline" className="ml-auto">{count}</Badge>
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-2 flex-1">
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-3 text-center">{emptyText}</p>
      ) : (
        items.slice(0, 8).map((e, i) => (renderRow ? renderRow(e, i) : <DeliveryRow key={`${e.type}-${e.id}-${i}`} e={e} />))
      )}
      {items.length > 8 && <p className="text-xs text-muted-foreground text-center pt-1">+{items.length - 8} more</p>}
    </CardContent>
  </Card>
);

const MetricCard = ({ label, value, icon: Icon, color, border, sub, onClick, testId }) => (
  <Card
    className={`border-l-4 ${border} ${onClick ? 'cursor-pointer hover:shadow-lg transition-all duration-200' : ''}`}
    onClick={onClick}
    data-testid={testId}
  >
    <CardContent className="pt-4 pb-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold font-heading mt-1">{value}</p>
          {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export const OpsOverview = ({ data, onJumpCalendar }) => {
  const navigate = useNavigate();
  const m = data.metrics;
  const bb = data.budget_burn;
  const [upWindow, setUpWindow] = useState(7);

  const todayD = new Date(); todayD.setHours(0, 0, 0, 0);
  const cutoff = new Date(todayD); cutoff.setDate(cutoff.getDate() + upWindow);
  const upcomingFiltered = (data.upcoming_all || []).filter((e) => {
    if (!e.start) return false;
    const s = new Date(e.start + 'T00:00:00');
    return s > todayD && s <= cutoff;
  });
  const windowOptions = [
    { v: 2, label: 'Next 2 days' },
    { v: 7, label: 'Next 7 days' },
    { v: 14, label: 'Next 2 weeks' },
    { v: 30, label: 'Next 1 month' },
    { v: 60, label: 'Next 2 months' },
  ];

  return (
    <div className="space-y-5">
      {/* Metric cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard label="Currently On" value={m.currently_on} icon={Activity} color="bg-green-100 text-green-600" border="border-l-green-500" sub="live today" testId="metric-currently-on" onClick={onJumpCalendar} />
        <MetricCard label="Next 7 Days" value={m.upcoming_7} icon={CalendarClock} color="bg-blue-100 text-blue-600" border="border-l-blue-500" sub={`+${m.upcoming_14 + m.upcoming_28} in 4 wks`} testId="metric-upcoming-7" onClick={onJumpCalendar} />
        <MetricCard label="Awaiting Feedback" value={m.awaiting_feedback} icon={MessageSquareWarning} color="bg-amber-100 text-amber-600" border="border-l-amber-500" sub="past, no rating" testId="metric-awaiting-feedback" />
        <MetricCard label="Content Triage" value={m.content_triage} icon={Flag} color="bg-rose-100 text-rose-600" border="border-l-rose-500" sub="linked & flagged" testId="metric-content-triage" onClick={() => navigate('/content-items?flag=triage')} />
        <MetricCard label="Overdue" value={m.overdue} icon={AlertTriangle} color="bg-red-100 text-red-600" border="border-l-red-500" sub="past, not closed" testId="metric-overdue" />
        <MetricCard label="POs Not Started" value={m.po_not_started_pos || 0} icon={ClipboardList} color="bg-lime-100 text-lime-600" border="border-l-lime-500" sub={`${m.po_not_started_items || 0} line items not started`} testId="metric-po-not-started" onClick={() => navigate('/purchase-orders')} />
      </div>

      {/* Currently On */}
      <SectionCard
        title="Currently On — Live Deliveries" icon={Activity} iconColor="text-green-600"
        count={m.currently_on} items={data.currently_on}
        emptyText="No deliveries running today" testId="section-currently-on"
      />

      {/* Upcoming — single scrollable table with a "Next X days" window selector */}
      <Card data-testid="section-upcoming">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-blue-600" />
              Upcoming Deliveries
              <Badge variant="outline" data-testid="upcoming-count">{upcomingFiltered.length}</Badge>
            </CardTitle>
            <Select value={String(upWindow)} onValueChange={(v) => setUpWindow(Number(v))}>
              <SelectTrigger className="w-[160px] h-9" data-testid="upcoming-window-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {windowOptions.map((o) => (
                  <SelectItem key={o.v} value={String(o.v)} data-testid={`upcoming-window-${o.v}`}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {upcomingFiltered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No deliveries scheduled in this window</p>
          ) : (
            <div className="max-h-[440px] overflow-y-auto rounded-lg border border-border" data-testid="upcoming-table">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/70 backdrop-blur z-10">
                  <tr className="text-left text-xs text-muted-foreground uppercase tracking-wide">
                    <th className="px-3 py-2 font-medium w-24">Type</th>
                    <th className="px-3 py-2 font-medium">Delivery</th>
                    <th className="px-3 py-2 font-medium hidden md:table-cell">Partner · Trainers</th>
                    <th className="px-3 py-2 font-medium text-right w-32">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingFiltered.map((e, i) => {
                    const tm = typeMeta(e.type);
                    return (
                      <tr
                        key={`${e.type}-${e.id}-${i}`}
                        onClick={() => navigate(e.url)}
                        className="border-t border-border hover:bg-muted/40 cursor-pointer transition-colors"
                        data-testid={`upcoming-row-${e.type}-${e.id}`}
                      >
                        <td className="px-3 py-2.5">
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: `${tm.color}1A`, color: tm.color }}>
                            {tm.label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <p className="font-medium truncate max-w-[280px]">{e.title}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[280px] md:hidden">
                            {e.partner_name}{e.trainers?.length ? ` · ${e.trainers.join(', ')}` : ''}
                          </p>
                        </td>
                        <td className="px-3 py-2.5 hidden md:table-cell text-muted-foreground">
                          <span className="truncate block max-w-[320px]">
                            {e.partner_name}{e.trainers?.length ? ` · ${e.trainers.join(', ')}` : ''}
                            {e.platform ? <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{e.platform}</span> : ''}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right whitespace-nowrap text-muted-foreground">{fmtDate(e.start)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recently completed + Awaiting feedback */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SectionCard title="Recently Completed (14 days)" icon={CheckCircle2} iconColor="text-green-600" count={m.recently_completed} items={data.recently_completed} emptyText="Nothing completed recently" testId="section-recently-completed" />
        <SectionCard title="Awaiting PM Feedback" icon={MessageSquareWarning} iconColor="text-amber-600" count={m.awaiting_feedback} items={data.awaiting_feedback} emptyText="All caught up on feedback" testId="section-awaiting-feedback" />
      </div>

      {/* Content triage + Overdue */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SectionCard title="Content Triage Blockers" icon={Flag} iconColor="text-rose-600" count={m.content_triage} items={data.content_triage} emptyText="No deliveries blocked on content triage" testId="section-content-triage" />
        <SectionCard title="Overdue — Needs Status Update" icon={AlertTriangle} iconColor="text-red-600" count={m.overdue} items={data.overdue_items} emptyText="Nothing overdue" testId="section-overdue" />
      </div>

      {/* Trainer gaps + PPRW readiness */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SectionCard title="Trainer Coverage Gaps (next 14 days)" icon={UserX} iconColor="text-orange-600" count={m.trainer_gaps} items={data.trainer_gaps} emptyText="Every upcoming delivery has a trainer" testId="section-trainer-gaps" />
        <SectionCard title="PPRW Readiness Blockers" icon={Clock} iconColor="text-violet-600" count={m.pprw_readiness} items={data.pprw_readiness} emptyText="No PPRW content/lab readiness risks" testId="section-pprw-readiness" />
      </div>

      {/* POE pending + PO status/budget */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SectionCard title="POE / Docs Pending" icon={FileWarning} iconColor="text-amber-600" count={m.poe_pending} items={data.poe_pending} emptyText="All proof-of-execution submitted" testId="section-poe-pending" />

        <Card data-testid="section-po-status">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Receipt className="w-4 h-4 text-lime-600" />
              PO Status & Budget Burn
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg bg-muted/40 py-2">
                <p className="text-lg font-bold font-heading">{fmtMoney(bb.contracted)}</p>
                <p className="text-[11px] text-muted-foreground">Contracted</p>
              </div>
              <div className="rounded-lg bg-muted/40 py-2">
                <p className="text-lg font-bold font-heading text-amber-600">{fmtMoney(bb.used)}</p>
                <p className="text-[11px] text-muted-foreground">Used ({bb.utilization}%)</p>
              </div>
              <div className="rounded-lg bg-muted/40 py-2">
                <p className="text-lg font-bold font-heading text-lime-600">{fmtMoney(bb.remaining)}</p>
                <p className="text-[11px] text-muted-foreground">Remaining</p>
              </div>
            </div>
            <div className="w-full h-2.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-lime-500" style={{ width: `${Math.min(100, bb.utilization)}%` }} />
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(data.po_status_counts).map(([k, v]) => (
                <button key={k} onClick={() => navigate('/purchase-orders')} className="text-xs px-2.5 py-1 rounded-full border border-border hover:bg-muted/50 capitalize" data-testid={`po-status-chip-${k}`}>
                  {k}: <span className="font-semibold">{v}</span>
                </button>
              ))}
            </div>
            {data.exhausted_pos.length > 0 && (
              <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2">
                <p className="text-xs font-medium text-red-700 mb-1">{data.exhausted_pos.length} exhausted PO(s) — awaiting extension</p>
                <div className="flex flex-wrap gap-1.5">
                  {data.exhausted_pos.map((po) => (
                    <button key={po.id} onClick={() => navigate('/purchase-orders')} className="text-[11px] px-2 py-0.5 rounded bg-white border border-red-200 text-red-700 hover:bg-red-100" data-testid={`exhausted-po-${po.id}`}>
                      {po.po_number}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OpsOverview;
